/**
 * Slider Component (Range Input)
 */

import { v4 as uuidv4 } from 'uuid'

import { IBuilderComponent } from '../../base'
import { formatFromVariableSelect, formatFromVariableText, formatSpacingFormat, formatWidthFormat } from '../../format-utils'
import type {
  ResponsiveValue,
  SpacingInputValue,
  VariableSelectValue,
  VariableTextValue,
} from '../../types'
import { deepMerge, parseSpacingFormat, parseToVariableText, parseWidthFormat } from '../../parser-utils'
import sliderTemplate from './slider.template.json'

export interface ISliderComponentSettings {
  // Data settings
  'slider-data-value': string // Column ID for the value
  'slider-data-end-value': string // Column ID for the end value (for range slider)
  'slider-data-label': VariableTextValue
  'slider-data-default-value': VariableSelectValue
  'slider-data-default-value-editable': boolean
  'slider-data-max-value': number
  'slider-data-step-value': number

  // Design settings
  'slider-design-width': number[]
  'slider-design-padding': SpacingInputValue
  'slider-design-thickness': number[]
  'slider-design-tick-mark': boolean
  'slider-design-show-min-max-values': boolean
  
  // Behavior settings
  'slider-behavior-required'?: boolean
}

export interface ISliderComponent extends IBuilderComponent {
  componentId: 'slider'
  groupId: 'form'
  settings: ISliderComponentSettings
}

/**
 * Slider AI Input
 */
export interface AiSliderInput {
  columnId: string // Required: maps to table column for the value
  endColumnId?: string // Optional: maps to table column for the end value (for range slider)
  label?: string[] // Format: ["text:Slider Label"] or ["text:Select ", "cr:value"]
  defaultValue?: number // Default value
  max?: number // Maximum value
  step?: number // Step value
  width?: string // Format: "w:100%" or "w:100% de:w:60%"
  padding?: string // Format: "p:16px"
  thickness?: number // Slider thickness
  showTickMarks?: boolean // Show tick marks
  showMinMaxValues?: boolean // Show min/max values
}

const SLIDER_DEFAULT_TEMPLATE = sliderTemplate as unknown as ISliderComponentSettings

export class SliderComp {
  getTemplate(): ISliderComponentSettings {
    return SLIDER_DEFAULT_TEMPLATE
  }

  /**
   * format slider component settings to AI slider input
   */
  format(settings: ISliderComponentSettings): AiSliderInput {
    const result: AiSliderInput = {
      columnId: settings['slider-data-value'],
    }

    // End Column ID (for range slider)
    if (settings['slider-data-end-value']) {
      result.endColumnId = settings['slider-data-end-value']
    }

    // Label
    const label = formatFromVariableText(settings['slider-data-label'])
    if (label && label.length > 0) {
      result.label = label
    }

    // Default Value (convert from VariableSelectValue to number)
    const defaultValue = formatFromVariableSelect(settings['slider-data-default-value'])
    if (defaultValue) {
      // Parse the defaultValue string (e.g., "text:50")
      if (defaultValue.startsWith('text:')) {
        const value = defaultValue.replace('text:', '')
        const numValue = parseInt(value, 10)
        if (!isNaN(numValue)) {
          result.defaultValue = numValue
        }
      }
    }

    // Max Value
    if (settings['slider-data-max-value'] !== undefined) {
      result.max = settings['slider-data-max-value']
    }

    // Step Value
    if (settings['slider-data-step-value'] !== undefined) {
      result.step = settings['slider-data-step-value']
    }

    // Width (convert from number[] to string format)
    if (settings['slider-design-width'] && settings['slider-design-width'].length > 0) {
      const widthValue = settings['slider-design-width'][0]
      result.width = `w:${widthValue}%`
    }

    // Padding (convert from SpacingInputValue to string format)
    if (settings['slider-design-padding']) {
      const padding = formatSpacingFormat(settings['slider-design-padding'] as any)
      if (padding) {
        result.padding = padding
      }
    }

    // Thickness (convert from number[] to number)
    if (settings['slider-design-thickness'] && settings['slider-design-thickness'].length > 0) {
      result.thickness = settings['slider-design-thickness'][0]
    }

    // Show Tick Marks
    if (settings['slider-design-tick-mark'] !== undefined) {
      result.showTickMarks = settings['slider-design-tick-mark']
    }

    // Show Min/Max Values
    if (settings['slider-design-show-min-max-values'] !== undefined) {
      result.showMinMaxValues = settings['slider-design-show-min-max-values']
    }

    return result
  }

  /**
   * parse AI slider input to slider component settings
   */
  parse(input: AiSliderInput): ISliderComponentSettings {
    const result = deepMerge(SLIDER_DEFAULT_TEMPLATE, {})

    // Data - Column ID (required for form fields)
    if (input.columnId) {
      result['slider-data-value'] = input.columnId
    }

    // Data - End Column ID (for range slider)
    if (input.endColumnId) {
      result['slider-data-end-value'] = input.endColumnId
    }

    // Data - Label
    if (input.label) {
      result['slider-data-label'] = parseToVariableText(input.label)
    }

    // Data - Default Value
    if (input.defaultValue !== undefined) {
      result['slider-data-default-value'] = {
        flag: 2048, // CustomText flag
        id: uuidv4(),
        value: String(input.defaultValue),
      }
    }

    // Data - Max Value
    if (input.max !== undefined) {
      result['slider-data-max-value'] = input.max
    }

    // Data - Step Value
    if (input.step !== undefined) {
      result['slider-data-step-value'] = input.step
    }

    // Design - Width
    if (input.width) {
      // Parse width format and extract phone value as number array
      // Format: "w:100%" -> [100], "w:100% de:w:60%" -> extract first value
      const widthMatch = input.width.match(/w:(\d+)/)
      if (widthMatch) {
        result['slider-design-width'] = [parseInt(widthMatch[1])]
      }
    }

    // Design - Padding
    if (input.padding) {
      const paddingParsed = parseSpacingFormat(input.padding)
      // Slider padding is stored as SpacingInputValue (not ResponsiveValue)
      result['slider-design-padding'] = paddingParsed.phone
    }

    // Design - Thickness
    if (input.thickness !== undefined) {
      result['slider-design-thickness'] = [input.thickness]
    }

    // Design - Tick Marks
    if (input.showTickMarks !== undefined) {
      result['slider-design-tick-mark'] = input.showTickMarks
    }

    // Design - Show Min/Max Values
    if (input.showMinMaxValues !== undefined) {
      result['slider-design-show-min-max-values'] = input.showMinMaxValues
    }


    return result
  }
}

